import * as React from 'react';
import {
  AppBar, Box, Toolbar, IconButton, Typography, Menu, MenuItem, Container,
  Avatar, Button, Tooltip, ListItemIcon, Badge, Divider, Fade, Paper
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CalculateIcon from '@mui/icons-material/Calculate';
import HomeIcon from '@mui/icons-material/Home';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BuildIcon from '@mui/icons-material/Build';
import StorefrontIcon from '@mui/icons-material/Storefront';
import GoogleTranslate from "../googleTranslate";
import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { loadFirebase } from '../../config/firebaseLazy';
import { Link, useNavigate, useLocation } from "react-router-dom";
import SupportButton from '../Buttons/supportButton';
import { HD_images } from '../../HD_images';

/*
 * These two pull in services/auth and services/dbService, which import the
 * Firebase SDK. The navbar renders on every page, so importing them statically
 * put Firebase in the initial bundle for everyone. Lazy-loading them moves
 * that weight into the same on-demand chunk as the SDK itself.
 */
const GoogleLoginButton = lazy(() => import('../GoogleLoginButton'));
const LogoutButton = lazy(() => import('../LogoutButton'));

const pages = [
  { name: 'Home', urlLink: '/home', icon: <HomeIcon fontSize="small" sx={{ mr: 1 }}/> },
  { name: 'Goods List', urlLink: '/goodsList', icon: <StorefrontIcon fontSize="small" sx={{ mr: 1 }}/> },
  { name: 'Add Friends', urlLink: '/addFriendsPage', icon: <ListAltIcon fontSize="small" sx={{ mr: 1 }}/> },
  { name: 'Production Buildings', urlLink: '/productionBuildingsList', icon: <BuildIcon fontSize="small" sx={{ mr: 1 }}/> }
];

const otherTools = [
  { name: 'Trees And Bushes List', urlLink: '/treesAndBushesList' },
  { name: 'Food List Maker', urlLink: '/foodListMaker', icon: <ListAltIcon fontSize="small" sx={{ mr: 1 }}/> },
  { name: 'Contact Us', urlLink: '/contactUs' },
];

const calculatorTools = [
  { name: 'Barn Size Calculator', urlLink: '/barnSizeCalculator', image: 'https://static.wikia.nocookie.net/hayday/images/f/f8/Barn.png' },
  { name: 'Buildings Slots Calculator', urlLink: '/buildingsSlotsCalculator', image: 'https://stan-bucket.sgp1.cdn.digitaloceanspaces.com/CMS/slotshd.jpeg?1740685802098' },
  { name: 'Buildings Coins Calculator', urlLink: '/buildingsCoinsCalculator', image: 'https://static.wikia.nocookie.net/hayday/images/f/f0/Coins.png' },
  { name: 'Silo Size Calculator', urlLink: '/siloSizeCalculator', image: 'https://static.wikia.nocookie.net/hayday/images/5/5f/Silo.png' },
  { name: 'Tackle Box Size Calculator', urlLink: '/tackleBoxSizeCalculator', image: 'https://static.wikia.nocookie.net/hayday/images/2/2c/Tackle_Box.png' },
  { name: 'Trees And Bushes Profit', urlLink: '/treesAndBushesProfitCalculator', image: 'https://stan-bucket.sgp1.cdn.digitaloceanspaces.com/CMS/chickenFunnyImage.webp' },
  { name: 'Town Tools Calculator', urlLink: '/townToolsCalculator', image: 'https://static.wikia.nocookie.net/hayday/images/e/e6/Town_Hall.png' },
  { name: 'Price Calculator', urlLink: '/priceCalculator', image: 'https://static.wikia.nocookie.net/hayday/images/f/f0/Coins.png' },
  { name: 'Deco Cost Calculator', urlLink: '/decoCostCalculator', image: 'https://static.wikia.nocookie.net/hayday/images/7/7a/Tractor.png' },
  { name: 'Mining Ores Calculator', urlLink: '/miningOresCalculator', image: 'https://static.wikia.nocookie.net/hayday/images/a/a0/Mine.png' },
  { name: 'Mining Diamonds Calculator', urlLink: '/miningDiamondsCalculator', image: 'https://static.wikia.nocookie.net/hayday/images/a/a0/Mine.png' },
  { name: 'XP Level Calculator', urlLink: '/xpLevelCalculator', image: HD_images['xpLevel34'] },
  { name: 'Goods Breakdown Calculator', urlLink: '/goodsBreakdown', image: HD_images['goodsBreakDownCalculator'] }
];

function ResponsiveAppBar() {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElCalcDesktop, setAnchorElCalcDesktop] = useState(null);
  const [anchorElCalcMobile, setAnchorElCalcMobile] = useState(null);
  const [anchorElToolsDesktop, setAnchorElToolsDesktop] = useState(null);
  const [anchorElToolsMobile, setAnchorElToolsMobile] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Firebase auth is fetched on demand rather than bundled into the initial
    // load. `user` stays null until it resolves, so the navbar shows the
    // logged-out state first and swaps in the account menu once auth reports
    // back — a brief flicker for signed-in users, in exchange for not shipping
    // the Firebase SDK to every visitor.
    let unsubscribe;
    let cancelled = false;
    let idleHandle;

    const initAuth = async () => {
      try {
        const [{ auth }, { onAuthStateChanged }] = await Promise.all([
          loadFirebase(),
          import(/* webpackChunkName: "firebase" */ 'firebase/auth'),
        ]);
        if (cancelled) return;
        unsubscribe = onAuthStateChanged(auth, (nextUser) => setUser(nextUser));
      } catch (err) {
        // Auth failing to load must not take the navbar down with it.
        console.error('Failed to initialise auth', err);
      }
    };

    // Firebase persists a signed-in session under a "firebase:authUser:*" key.
    // When one is present the user is probably signed in, so fetch auth right
    // away to keep the logged-out flash short. Otherwise there's nothing to
    // restore, so defer to idle time and let the page finish rendering first.
    //
    // The check is only a scheduling hint, never a source of truth —
    // onAuthStateChanged still decides who is signed in. If the hint is wrong
    // the worst case is auth loading a moment later than ideal, not someone
    // being shown as logged out when they aren't.
    let looksSignedIn = false;
    try {
      looksSignedIn = Object.keys(window.localStorage).some((k) =>
        k.startsWith('firebase:authUser:')
      );
    } catch (err) {
      // Storage can throw in private mode; fall through to the idle path.
    }

    if (looksSignedIn) {
      initAuth();
    } else if (typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(initAuth, { timeout: 3000 });
    } else {
      idleHandle = setTimeout(initAuth, 1200);
    }

    return () => {
      cancelled = true;
      if (idleHandle) {
        if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idleHandle);
        clearTimeout(idleHandle);
      }
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const handleLoginSuccess = (user) => {
    console.log('Logged in user:', user);
  };

  
  
  // Close all menus
  const closeAllMenus = useCallback(() => {
    setAnchorElNav(null);
    setAnchorElCalcDesktop(null);
    setAnchorElCalcMobile(null);
    setAnchorElToolsDesktop(null);
    setAnchorElToolsMobile(null);
    setAnchorElUser(null);
  }, []);

  const handleLogout = useCallback(() => {
    setUser(null);
    closeAllMenus();
    // If you're using react-router, you might want to navigate to home
    navigate('/home');
  }, [closeAllMenus, navigate]);

  // Handle menu item click with navigation
  const handleMenuItemClick = useCallback((urlLink) => {
    closeAllMenus();
    navigate(urlLink);
  }, [closeAllMenus, navigate]);
  
  // Check if route is active
  const isActive = useCallback((path) => {
    return location.pathname === path;
  }, [location]);

  return (
    <AppBar 
      position="sticky" 
      sx={{ 
        background: 'linear-gradient(to bottom, #3a8c3a 0%, #2e7d32 100%)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        borderBottom: '3px solid #ffcc00'
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ minHeight: { xs: 70, md: 80 }, justifyContent: "space-between" }}>

          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="h5"
              noWrap
              component={Link}
              to="/home"
              onClick={closeAllMenus}
              sx={{
                display: { xs: 'none', md: 'flex' },
                fontWeight: 700,
                letterSpacing: '.1rem',
                color: '#ffcc00',
                textDecoration: 'none',
                textShadow: '2px 2px 4px rgba(0,0,0,0.4)',
                fontSize: '1.8rem',
                whiteSpace: 'nowrap',
                minWidth: '140px',
                fontFamily: '"Poppins", "Roboto", "Arial", sans-serif',
                position: 'relative',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  width: '35%',
                  height: '2px',
                  bottom: '-3px',
                  left: '2px',
                  backgroundColor: '#ffcc00',
                  borderRadius: '4px'
                },
                mr: 3
              }}
            >
              HAY DAY
            </Typography>
          </Box>
          

          {/* Desktop Navbar */}
          <Box sx={{ 
            flexGrow: 1, 
            display: { xs: 'none', md: 'flex' }, 
            gap: 1.5, 
            ml: 2,
            alignItems: 'center'
          }}>
            {pages.map((page) => (
              <Button 
                key={page.name} 
                component={Link} 
                to={page.urlLink} 
                onClick={closeAllMenus}
                startIcon={page.icon}
                sx={{ 
                  color: isActive(page.urlLink) ? '#ffcc00' : 'white',
                  border: isActive(page.urlLink) ? '1px solid #ffcc00' : 'none',
                  borderRadius: '8px',
                  px: 1.5,
                  py: 0.8,
                  fontSize: '0.9rem',
                  fontWeight: isActive(page.urlLink) ? 700 : 500,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  },
                  textTransform: 'none',
                  transition: 'all 0.2s ease'
                }}
              >
                {page.name}
              </Button>
            ))}

            {/* Calculators Dropdown */}
            <Button 
              onClick={(e) => setAnchorElCalcDesktop(e.currentTarget)} 
              endIcon={<ArrowDropDownIcon />}
              startIcon={<CalculateIcon />}
              sx={{ 
                color: 'white',
                ml: 1.5,
                borderRadius: '8px',
                px: 1.5,
                py: 0.8,
                fontSize: '0.9rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                },
                textTransform: 'none'
              }}
            >
              Calculators
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
  {/* Other navbar items */}
  <SupportButton 
    variant="icon-only" 
    animate={true} 
    tooltip="Like this tool? Consider supporting us!" 
  />
  {/* User menu, etc. */}
</Box>
            <Menu 
              anchorEl={anchorElCalcDesktop} 
              open={Boolean(anchorElCalcDesktop)} 
              onClose={() => setAnchorElCalcDesktop(null)}
              MenuListProps={{ 'aria-labelledby': 'calculator-button' }}
              PaperProps={{
                elevation: 6,
                sx: {
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  mt: 0.5,
                  overflow: 'visible',
                  border: '1px solid #e0e0e0',
                  width: 280,
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderTop: '1px solid #e0e0e0',
                    borderLeft: '1px solid #e0e0e0',
                  },
                },
              }}
              TransitionComponent={Fade}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Box 
                sx={{ 
                  p: 1.5, 
                  pb: 1, 
                  display: 'flex', 
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(46, 125, 50, 0.1)'
                  }
                }}
                onClick={() => handleMenuItemClick('/calculators')}
              >
                <CalculateIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>All Calculators</Typography>
              </Box>
              <Divider sx={{ my: 0.5, mx: 1.6 }} />
              {calculatorTools.map((tool) => (
                <MenuItem 
                  key={tool.name} 
                  onClick={() => handleMenuItemClick(tool.urlLink)}
                  sx={{ 
                    borderRadius: 1, 
                    mx: 0.5, 
                    py: 0.75, 
                    fontSize: '0.9rem',
                    '&:hover': {
                      backgroundColor: 'rgba(46, 125, 50, 0.1)'
                    }
                  }}
                >
                  <Avatar
                    variant="rounded"
                    src={tool.image}
                    alt={tool.name}
                    sx={{ width: 28, height: 28, mr: 1.25, borderRadius: 1 }}
                  />
                  {tool.name}
                </MenuItem>
              ))}
            </Menu>

            {/* Other Tools Dropdown */}
            <Button 
              onClick={(e) => setAnchorElToolsDesktop(e.currentTarget)} 
              endIcon={<ArrowDropDownIcon />}
              startIcon={<BuildIcon />}
              sx={{ 
                color: 'white',
                borderRadius: '8px',
                px: 1.5,
                py: 0.8,
                fontSize: '0.9rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                },
                textTransform: 'none'
              }}
            >
              Other Tools
            </Button>
            <Menu 
              anchorEl={anchorElToolsDesktop} 
              open={Boolean(anchorElToolsDesktop)} 
              onClose={() => setAnchorElToolsDesktop(null)}
              MenuListProps={{ 'aria-labelledby': 'tools-button' }}
              PaperProps={{
                elevation: 6,
                sx: {
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  mt: 0.5,
                  overflow: 'visible',
                  border: '1px solid #e0e0e0',
                  width: 200,
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderTop: '1px solid #e0e0e0',
                    borderLeft: '1px solid #e0e0e0',
                  },
                },
              }}
              TransitionComponent={Fade}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Box sx={{ p: 1.5, pb: 1, display: 'flex', alignItems: 'center' }}>
                <BuildIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Other Tools</Typography>
              </Box>
              <Divider sx={{ my: 0.5, mx: 1.5 }} />
              {otherTools.map((tool) => (
                <MenuItem 
                  key={tool.name} 
                  onClick={() => handleMenuItemClick(tool.urlLink)}
                  sx={{ 
                    borderRadius: 1, 
                    mx: 0.5, 
                    py: 0.75, 
                    fontSize: '0.9rem',
                    '&:hover': {
                      backgroundColor: 'rgba(46, 125, 50, 0.1)'
                    }
                  }}
                >
                  {tool.name}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Mobile Menu */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton 
              size="large" 
              onClick={(e) => setAnchorElNav(e.currentTarget)} 
              color="inherit"
              sx={{ 
                bgcolor: anchorElNav ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
              }}
            >
              <MenuIcon />
            </IconButton>
            <Menu 
              anchorEl={anchorElNav} 
              open={Boolean(anchorElNav)} 
              onClose={() => setAnchorElNav(null)}
              PaperProps={{
                elevation: 8,
                sx: {
                  maxHeight: '80vh',
                  width: '230px',
                  borderRadius: '12px',
                  border: '1px solid #e0e0e0',
                  overflow: 'hidden'
                },
              }}
              TransitionComponent={Fade}
              transformOrigin={{ horizontal: 'left', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            >
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#2e7d32',
                color: 'white',
                textAlign: 'center'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#ffcc00',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.4)'
                  }}
                >
                  HAY DAY
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                  Calculator & Tools
                </Typography>
                <Box sx={{ textAlign: 'center', mt: 1 }}>
  <SupportButton 
    variant="default" 
    animate={true} 
    sx={{ mt: 0 }} 
    customText="Support US" 
  />
</Box>
              </Box>
              <Divider />
              
              {pages.map((page) => (
                <MenuItem 
                  key={page.name} 
                  onClick={() => handleMenuItemClick(page.urlLink)}
                  sx={{ 
                    py: 1.2, 
                    borderLeft: isActive(page.urlLink) ? '4px solid #2e7d32' : '4px solid transparent',
                    backgroundColor: isActive(page.urlLink) ? 'rgba(46, 125, 50, 0.1)' : 'transparent'
                  }}
                >
                  <ListItemIcon>
                    {page.icon}
                  </ListItemIcon>
                  <Typography 
                    sx={{ 
                      fontWeight: isActive(page.urlLink) ? 600 : 400,
                      color: isActive(page.urlLink) ? '#2e7d32' : 'inherit'
                    }}
                  >
                    {page.name}
                  </Typography>
                </MenuItem>
              ))}
              
              <Divider sx={{ my: 1 }} />
              
              {/* Mobile - Calculators Submenu */}
              <MenuItem 
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorElCalcMobile(e.currentTarget);
                }}
                sx={{ py: 1.2 }}
              >
                <ListItemIcon>
                  <CalculateIcon fontSize="small" />
                </ListItemIcon>
                <Typography>Calculators</Typography>
                <ArrowRightIcon sx={{ ml: 'auto', fontSize: '1.2rem' }} />
              </MenuItem>
              
              {/* Mobile - Other Tools Submenu */}
              <MenuItem 
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorElToolsMobile(e.currentTarget);
                }}
                sx={{ py: 1.2 }}
              >
                <ListItemIcon>
                  <BuildIcon fontSize="small" />
                </ListItemIcon>
                <Typography>Other Tools</Typography>
                <ArrowRightIcon sx={{ ml: 'auto', fontSize: '1.2rem' }} />
              </MenuItem>
            </Menu>
            
            {/* Mobile Calculators Submenu */}
            <Menu 
              anchorEl={anchorElCalcMobile}
              open={Boolean(anchorElCalcMobile) && Boolean(anchorElNav)}
              onClose={() => setAnchorElCalcMobile(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                elevation: 8,
                sx: {
                  maxHeight: '70vh',
                  width: '220px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid #e0e0e0',
                },
              }}
            >
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#2e7d32',
                color: 'white',
                textAlign: 'center'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  <CalculateIcon sx={{ mr: 1, verticalAlign: 'text-top' }} />
                  Calculators
                </Typography>
              </Box>
              
              <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pt: 1 }}>
                {calculatorTools.map((tool) => (
                  <MenuItem 
                    key={tool.name} 
                    onClick={() => handleMenuItemClick(tool.urlLink)}
                    sx={{ 
                      py: 1.2,
                      px: 2
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      src={tool.image}
                      alt={tool.name}
                      sx={{ width: 26, height: 26, mr: 1.25, borderRadius: 1 }}
                    />
                    {tool.name}
                  </MenuItem>
                ))}
              </Box>
            </Menu>
            
            {/* Mobile Other Tools Submenu */}
            <Menu 
              anchorEl={anchorElToolsMobile}
              open={Boolean(anchorElToolsMobile) && Boolean(anchorElNav)}
              onClose={() => setAnchorElToolsMobile(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                elevation: 8,
                sx: {
                  maxHeight: '70vh',
                  width: '220px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid #e0e0e0'
                },
              }}
            >
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#2e7d32',
                color: 'white',
                textAlign: 'center'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  <BuildIcon sx={{ mr: 1, verticalAlign: 'text-top' }} />
                  Other Tools
                </Typography>
              </Box>
              
              <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pt: 1 }}>
                {otherTools.map((tool) => (
                  <MenuItem 
                    key={tool.name} 
                    onClick={() => handleMenuItemClick(tool.urlLink)}
                    sx={{ py: 1.2 }}
                  >
                    {tool.name}
                  </MenuItem>
                ))}
              </Box>
            </Menu>
          </Box>

          {/* Mobile Logo (Center) */}
          <Typography
            variant="h6"
            noWrap
            component={Link}
            to="/home"
            onClick={closeAllMenus}
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              fontWeight: 700,
              letterSpacing: '.1rem',
              color: '#ffcc00',
              textDecoration: 'none',
              textShadow: '1px 1px 2px rgba(0,0,0,0.4)',
              fontSize: '1.4rem',
              fontFamily: '"Poppins", "Roboto", "Arial", sans-serif',
            }}
          >
            HAY DAY
          </Typography>

          {/* User & Language */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: '24px',
            px: { xs: 1, md: 2 },
            py: 0.5
          }}>
            <GoogleTranslate />

            {user ? (
              <>
                <Tooltip title={user.displayName || "Account"}>
                  <IconButton 
                    onClick={(e) => setAnchorElUser(e.currentTarget)}
                    sx={{ 
                      p: 0.5,
                      border: '2px solid #ffcc00',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 204, 0, 0.1)'
                      }
                    }}
                  >
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      variant="dot"
                      color="secondary"
                    >
                      <Avatar 
                        src={user.photoURL} 
                        sx={{ 
                          width: 36, 
                          height: 36,
                          boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                        }} 
                      />
                    </Badge>
                  </IconButton>
                </Tooltip>
                <Menu 
                  anchorEl={anchorElUser} 
                  open={Boolean(anchorElUser)} 
                  onClose={() => setAnchorElUser(null)}
                  PaperProps={{
                    elevation: 6,
                    sx: {
                      borderRadius: 2,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      mt: 1.5,
                      minWidth: 180,
                      overflow: 'visible',
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 16,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                        borderTop: '1px solid #e0e0e0',
                        borderLeft: '1px solid #e0e0e0',
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <Box sx={{ 
                    p: 2, 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderBottom: '1px solid #eee'
                  }}>
                    <Avatar 
                      src={user.photoURL} 
                      sx={{ 
                        width: 60, 
                        height: 60, 
                        mb: 1,
                        border: '2px solid #ffcc00'
                      }}
                    />
                    <Typography variant="subtitle1" fontWeight={600}>
                      {user.displayName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                  
                  <MenuItem 
  onClick={() => {
    setAnchorElUser(null);
  }}
  sx={{ mt: 1 }}
>
  <Suspense fallback={null}>
    <LogoutButton
      onLogout={handleLogout}
      userId={user?.uid}
    />
  </Suspense>
</MenuItem>
                </Menu>
              </>
            ) : (
              <Suspense fallback={null}>
                <GoogleLoginButton onSuccess={handleLoginSuccess} />
              </Suspense>
            )}
          </Box>

        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default ResponsiveAppBar;